<template>
  <div
    class="relative flex w-full min-w-0 min-h-0 flex-col pt-[calc(env(safe-area-inset-top)+68px)] px-4 pb-0! tablet-h:mt-[18px] tablet-h:pt-0 tablet-h:pl-10 tablet-h:pr-[26px]"
  >
    <AlertBanner class="mb-[15px] tablet-h:mb-[18px]" />

    <!-- Mobile header -->
    <div v-if="isMobileHeader" class="tablet-h:hidden">
      <h1
        v-if="isGroupView"
        class="text-[24px] leading-[30px] font-medium text-gray-800"
      >
        {{ groupReportCountLabel }}
      </h1>

      <template v-else>
        <h1 class="text-[24px] leading-[30px] font-medium text-gray-800">
          Infringement Docket
        </h1>

        <div class="mt-2 text-[14px] leading-[18px] text-gray-800">
          <p>Infringement of your work, detected by DIIS</p>
          <p class="flex items-center gap-x-1">
            Powered by
            <DacrAIIcon class="h-[13px] w-auto" aria-label="Dacr AI" />
          </p>
        </div>

        <Button
          variant="primary"
          size="lg"
          :icon="FileAddIcon"
          :disabled="!hasLoaded"
          class="mt-5"
          @click="handleCreateReportClick"
        >
          Infringement report
        </Button>

        <TabCards v-model="activeTabKey" class="mt-5">
          <TabCards.Card value="all" label="All">
            {{ !hasLoaded ? '-' : formatNumber(total) }}
          </TabCards.Card>
          <TabCards.Card value="flags" label="Flagged">
            {{ !hasLoaded ? '-' : formatNumber(totalFlags) }}
          </TabCards.Card>
          <TabCards.Card value="reports" label="Reports">
            {{ !hasLoaded ? '-' : formatNumber(totalReports) }}
          </TabCards.Card>
        </TabCards>
      </template>
    </div>

    <!-- Desktop header (pending redesign) -->
    <div
      v-if="isDesktopHeader"
      class="hidden tablet-h:flex tablet-h:flex-col mb-3"
    >
      <template v-if="isGroupView">
        <RouterLink v-slot="{ href, navigate }" to="/infringement" custom>
          <Link
            :href="href"
            :leading-icon="ChevronLeftIcon"
            class="mb-2"
            @click="navigate"
          >
            Infringement Docket
          </Link>
        </RouterLink>
        <h1 class="text-[22px] leading-[26px] font-medium text-gray-800">
          {{ groupReportCountLabel }}
        </h1>
      </template>

      <template v-else>
        <h1 class="text-[22px] leading-[26px] font-medium text-gray-800">
          Infringement Docket
        </h1>
        <p class="mt-1.5 text-[14px] leading-[18px] text-gray-800">
          Infringement of your work, detected by DIIS. Powered by
          <DacrAIIcon
            class="inline-block h-[13px] w-auto align-[-1px]"
            aria-label="Dacr AI"
          />
        </p>

        <TabCards v-model="activeTabKey" class="mt-4">
          <TabCards.Card value="all" label="All" class="w-[150px]">
            {{ !hasLoaded ? '-' : formatNumber(total) }}
          </TabCards.Card>
          <TabCards.Card value="flags" label="Flagged" class="w-[168px]">
            {{ !hasLoaded ? '-' : formatNumber(totalFlags) }}
          </TabCards.Card>
          <TabCards.Card value="reports" label="Reports" class="w-[168px]">
            {{ !hasLoaded ? '-' : formatNumber(totalReports) }}
          </TabCards.Card>
        </TabCards>

        <div class="mt-4 flex items-center justify-between">
          <Button
            variant="primary"
            size="md"
            :icon="FileAddIcon"
            :disabled="!hasLoaded"
            @click="handleCreateReportClick"
          >
            Infringement report
          </Button>

          <button
            v-if="hasLoaded"
            type="button"
            class="flex h-[30px] cursor-pointer items-center gap-x-1.5 rounded-lg border border-gray-200/75 bg-gray-200/13 px-2.5 text-[14px] leading-[18px] text-gray-500 transition-colors hover:border-gray-300"
            @click="handleBuyCreditsClick"
          >
            Report credits
            <span class="font-medium text-gray-800">{{
              formatNumber(credits)
            }}</span>
            <ChevronRightIcon class="size-2.5 text-gray-800" />
          </button>
        </div>
      </template>
    </div>

    <!-- Mobile list -->
    <div
      class="tablet-h:hidden relative -mr-4 mt-5 flex min-h-0 flex-1 flex-col border-t border-[#efefef]"
    >
      <div
        v-if="hasLoaded && isReportProcessing"
        class="border--gradient pointer-events-none absolute inset-x-0 -top-px z-30 h-px"
      />

      <NoInfringements
        v-if="!loading && !infringements.length"
        class="mr-4 min-h-0 flex-1"
      />

      <div
        v-else
        ref="mobileScrollElement"
        class="min-h-0 flex-1 overflow-auto pb-[calc(env(safe-area-inset-bottom)+100px)]"
        @scroll="handleScroll"
      >
        <template v-if="loading && !infringements.length">
          <div v-for="n in 6" :key="n" class="relative h-[100px] py-2.5 pr-5">
            <div
              class="absolute right-0 bottom-0 left-[37px] h-px bg-[#2d2d2d]/8"
            />
            <div class="flex items-center justify-between">
              <Skeleton class="h-[20px] w-[110px]" />
              <Skeleton class="h-[20px] w-[80px]" />
            </div>
            <div class="mt-2.5 flex items-center gap-x-3">
              <Skeleton class="size-6 shrink-0" />
              <Skeleton class="h-[14px] w-[160px]" />
            </div>
            <div class="mt-2 flex items-center gap-x-3">
              <Skeleton class="size-6 shrink-0" />
              <Skeleton class="h-[14px] w-[130px]" />
            </div>
          </div>
        </template>

        <template v-else>
          <InfringementListItem
            v-for="infringement in infringements"
            :key="infringement.id"
            :infringement="infringement"
          />
        </template>
      </div>
    </div>

    <!-- Desktop table -->
    <div class="hidden tablet-h:flex flex-col flex-1 overflow-hidden">
      <InfringementListTable
        ref="desktopTable"
        :infringements="infringements"
        :processing="hasLoaded && isReportProcessing"
        :loading="loading && !infringements.length"
        :empty="!loading && !infringements.length"
        :sort="activeSort"
        @scroll="handleScroll"
        @create="handleCreateReportClick"
        @sort="handleSortChange"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { ref as sessionRef } from 'vue'

const sessionHasLoaded = sessionRef(false)
const sessionTotalFlags = sessionRef<number>()
const sessionTotalReports = sessionRef<number>()
</script>

<script setup lang="ts">
import InfringementListItem from '@/features/infringement/components/InfringementListItem.vue'
import NoInfringements from '@/features/infringement/components/NoInfringements.vue'
import InfringementListTable from '@/features/infringement/components/InfringementListTable.vue'
import AlertBanner from '@/shared/components/AlertBanner.vue'
import { openDialog, closeAllDialogs, Dialog } from '@/shared/dialogs/dialog'
import { graphql } from '@/shared/graphql/router'
import {
  InfringementLabel,
  InfringementSortField,
  SortDirection as RouterSortDirection,
} from '@/shared/graphql/router/graphql'
import { useInfringementProcessingStore } from '@/features/infringement/store'
import { useReportProcessing } from '@/features/infringement/composables/useReportProcessing'
import { useUserStore } from '@/shared/stores/user'
import { useDevice } from '@/shared/composables/useDevice'
import { Button, Link, Skeleton, TabCards } from '@dacr/library'
import {
  DacrAIIcon,
  FileAddIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@dacr/icons'
import { useQuery } from '@vue/apollo-composable'
import {
  computed,
  nextTick,
  ref,
  watch,
  type ComponentPublicInstance,
} from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { formatNumber } from '@/shared/utils/number'
import pluralize from 'pluralize'

const userStore = useUserStore()
const processingStore = useInfringementProcessingStore()
const device = useDevice()
const route = useRoute()
const router = useRouter()

const isGroupView = computed(() => route.name === 'InfringementGroupView')
const isMobileHeader = computed(
  () => device.isMobile.value || device.isTabletVertical.value
)
const isDesktopHeader = computed(
  () => device.isTabletHorizontal.value || device.isDesktop.value
)

const groupFilter = computed(() => {
  if (!isGroupView.value) return undefined
  return {
    id: route.params.groupId as string,
    timestamp: Number(route.params.timestamp),
  }
})

const activeTabKey = computed<string>({
  get: () => {
    const tab = route.query.tab
    return tab === 'flags' || tab === 'reports' ? tab : 'all'
  },
  set: (value) => {
    router.replace({
      query: {
        ...route.query,
        tab: value === 'all' ? undefined : value,
      },
    })
  },
})

const activeTab = computed<InfringementLabel | null>(() =>
  activeTabKey.value === 'flags'
    ? InfringementLabel.Flag
    : activeTabKey.value === 'reports'
      ? InfringementLabel.Report
      : null
)

type SortField = 'TIMESTAMP'
type SortDirection = 'ASC' | 'DESC'

type InfringementSort = {
  field: SortField
  direction: SortDirection
}

const activeSort = ref<InfringementSort>({
  field: 'TIMESTAMP',
  direction: 'DESC',
})

const handleSortChange = (sort: InfringementSort) => {
  activeSort.value = sort
}

const querySort = (sort: InfringementSort) => ({
  field: InfringementSortField.Timestamp,
  direction:
    sort.direction === 'ASC'
      ? RouterSortDirection.Asc
      : RouterSortDirection.Desc,
})

const pageSize = computed(() => (groupFilter.value ? 100 : 20))

const { result, loading, fetchMore, query } = useQuery(
  graphql(`
    query InfringementList($input: InfringementsInput) {
      infringements(input: $input) {
        totalFlags
        totalReports
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            id
            label
            timestamp
            copyrights {
              id
              name
              asset {
                previewImageUrl
              }
              type
              copyright {
                id
              }
            }
            infringingFile {
              id
              name
              copyrightId
              asset {
                previewImageUrl
              }
              type
            }
            matches {
              score
              level
              breakdown {
                level
                score
                type
              }
            }
            report {
              id
              status
            }
          }
        }
      }
    }
  `),
  () => ({
    input: {
      first: pageSize.value,
      sort: querySort(activeSort.value),
      filterBy: {
        ...(activeTab.value && { type: activeTab.value }),
        ...(groupFilter.value && { group: groupFilter.value }),
      },
    },
  }),
  {
    clientId: 'router',
    fetchPolicy: isGroupView.value ? 'no-cache' : 'cache-and-network',
  }
)

const hasLoaded = sessionHasLoaded
const cachedTotalFlags = sessionTotalFlags
const cachedTotalReports = sessionTotalReports
const isFetchingMore = ref(false)
const mobileScrollElement = ref<HTMLElement | null>(null)
const desktopTable = ref<
  (ComponentPublicInstance & { scrollElement: HTMLElement | null }) | null
>(null)

watch(
  result,
  (value) => {
    if (value) {
      hasLoaded.value = true

      processingStore.reconcile(value.infringements.edges.map((e) => e.node))

      if (!groupFilter.value) {
        cachedTotalFlags.value = value.infringements.totalFlags
        cachedTotalReports.value = value.infringements.totalReports
      }
    }
  },
  { immediate: true }
)

const queryInfringements = computed(
  () => result.value?.infringements.edges.map((edge) => edge.node) ?? []
)

const isReportProcessing = useReportProcessing(queryInfringements)

watch(
  [isReportProcessing, query],
  ([processing, observable]) => {
    if (processing) observable?.startPolling(10_000)
    else observable?.stopPolling()
  },
  { immediate: true }
)

const includeProcessingCounts = computed(() => !groupFilter.value)

const infringements = computed(() =>
  groupFilter.value
    ? queryInfringements.value
    : activeTab.value === InfringementLabel.Flag
      ? processingStore.withoutPendingReports(queryInfringements.value)
      : processingStore.merge(queryInfringements.value)
)

const groupReportCount = computed(() =>
  hasLoaded.value ? infringements.value.length : 0
)

const groupReportCountLabel = computed(
  () =>
    `${formatNumber(groupReportCount.value)} ${pluralize('report', groupReportCount.value)}`
)

const pendingReportCount = computed(() =>
  includeProcessingCounts.value ? processingStore.pendingReportCount : 0
)

const pendingFlagReplacementCount = computed(() =>
  includeProcessingCounts.value
    ? processingStore.pendingFlagReplacementCount
    : 0
)

const totalFlags = computed(() =>
  Math.max(
    0,
    (cachedTotalFlags.value ?? result.value?.infringements.totalFlags ?? 0) -
      pendingFlagReplacementCount.value
  )
)

const totalReports = computed(() => {
  const base =
    cachedTotalReports.value ?? result.value?.infringements.totalReports ?? 0
  return base + pendingReportCount.value
})

const total = computed(
  () => (totalFlags.value || 0) + (totalReports.value || 0)
)

const credits = computed(() => userStore.user?.reportCredits || 0)

const fetchNextPage = async () => {
  const pageInfo = result.value?.infringements.pageInfo

  if (pageInfo?.hasNextPage && !loading.value && !isFetchingMore.value) {
    isFetchingMore.value = true
    let fetchedNextPage = false
    let addedEdges = 0

    try {
      await fetchMore({
        variables: {
          input: {
            first: pageSize.value,
            after: pageInfo.endCursor,
            sort: querySort(activeSort.value),
            filterBy: {
              ...(activeTab.value && { type: activeTab.value }),
              ...(groupFilter.value && { group: groupFilter.value }),
            },
          },
        },
        updateQuery: (previousData, { fetchMoreResult: newData }) => {
          if (!newData) return previousData
          addedEdges = newData.infringements.edges.length

          return {
            __typename: 'Query' as const,
            infringements: {
              ...previousData.infringements,
              edges: [
                ...previousData.infringements.edges,
                ...newData.infringements.edges,
              ],
              pageInfo: newData.infringements.pageInfo,
            },
          }
        },
      })
      fetchedNextPage = addedEdges > 0
    } finally {
      isFetchingMore.value = false
    }

    if (fetchedNextPage && groupFilter.value) {
      fetchNextPage()
    }

    return fetchedNextPage
  }

  return false
}

const scrollElement = () =>
  isDesktopHeader.value
    ? (desktopTable.value?.scrollElement ?? null)
    : mobileScrollElement.value

const fillViewport = async () => {
  if (loading.value || isFetchingMore.value) return

  const element = scrollElement()
  const pageInfo = result.value?.infringements.pageInfo
  if (!element || !pageInfo?.hasNextPage) return

  if (!element.isConnected || !element.clientHeight) return

  if (element.scrollHeight > element.clientHeight + 1) return

  const fetchedNextPage = await fetchNextPage()
  if (fetchedNextPage) queueViewportFillCheck()
}

let viewportFillCheckQueued = false

const queueViewportFillCheck = () => {
  if (viewportFillCheckQueued) return

  viewportFillCheckQueued = true
  nextTick(() => {
    viewportFillCheckQueued = false
    fillViewport()
  })
}

watch(
  [result, loading],
  () => {
    if (groupFilter.value && !loading.value) {
      fetchNextPage()
    }

    queueViewportFillCheck()
  },
  { immediate: true }
)

watch(
  [() => infringements.value.length, isDesktopHeader],
  () => queueViewportFillCheck(),
  { flush: 'post' }
)

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const elementHeight = element.scrollHeight
  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / elementHeight >= 0.5) {
    fetchNextPage()
  }
}

const handleBuyCreditsClick = () =>
  openDialog(Dialog.BuyReportCredits, {
    props: { afterPurchase: () => closeAllDialogs() },
  })

const handleCreateReportClick = () => {
  if (!totalReports.value) {
    return openDialog(Dialog.InfringementReport)
  }

  if (credits.value) {
    return openDialog(Dialog.CreateReport)
  }

  openDialog(Dialog.BuyReportCredits)
}
</script>
